/**
 * Tenant URL Resolution
 *
 * Resolves the active tenant from (in precedence order):
 *   1. the URL path prefix /tenant/:code/
 *   2. the ?tenantId= query param (DK iframe entry — DKPLAT-467)
 *   3. the VITE_TENANT_SLUG environment variable (default 'dk')
 *
 * URL patterns:
 *   https://example.com/tenant/nbc/...       → tenant "nbc"  (path wins)
 *   https://example.com/pickem?tenantId=sky  → tenant "sky"  (query)
 *   https://example.com/...                  → default from env
 *
 * Used by all frontend apps (pickem, squares, daily-reveal, lobby).
 *
 * DK loads our games via a single host with a path segment per game and the
 * game/tenant on the query string, e.g.
 *   https://freetoplay.draftkings.com/pickem?gameId=<guid>&tenantId=<slug>
 * `tenantId` is our tenant SLUG (dk/sky/…), so it drops straight into the
 * X-Tenant-Slug header. `gameId` is our game GUID. The resolver runs at module
 * load, when window.location.search is already available, so a query tenant
 * flows into the header on the first request with no redirect/async.
 */

const TENANT_PATH_REGEX = /^\/tenant\/([a-zA-Z0-9_-]+)/;
const GAME_ID_PATH_REGEX = /^(?:\/tenant\/[a-zA-Z0-9_-]+)?\/game\/([a-zA-Z0-9-]+)/;

/** Query-string param names DK passes on the iframe entry URL (DKPLAT-467) */
export const TENANT_QUERY_PARAM = 'tenantId';
export const GAME_QUERY_PARAM = 'gameId';

/** Read + trim a query-string param from the current URL, or null. */
function getQueryParam(name: string): string | null {
  const raw = new URLSearchParams(window.location.search).get(name);
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/** Resolve tenant code: /tenant/:code path → ?tenantId= query → env fallback */
export function resolveTenantFromUrl(): string {
  const match = window.location.pathname.match(TENANT_PATH_REGEX);
  if (match) {
    return match[1].toLowerCase();
  }
  const queryTenant = getQueryParam(TENANT_QUERY_PARAM);
  if (queryTenant) {
    return queryTenant.toLowerCase();
  }
  return import.meta.env?.VITE_TENANT_SLUG || 'dk';
}

/** Get the tenant base path prefix (e.g. "/tenant/nbc" or "") */
export function getTenantBasePath(): string {
  const match = window.location.pathname.match(TENANT_PATH_REGEX);
  if (match) {
    return `/tenant/${match[1].toLowerCase()}`;
  }
  return '';
}

/** Check if the current URL uses tenant path routing */
export function isUrlTenantMode(): boolean {
  return TENANT_PATH_REGEX.test(window.location.pathname);
}

/**
 * Resolve the game id: /game/:id (or /tenant/:code/game/:id) path →
 * ?gameId= query param (DK iframe entry — DKPLAT-467) → null.
 * The game id is our game GUID.
 */
export function resolveGameIdFromUrl(): string | null {
  const match = window.location.pathname.match(GAME_ID_PATH_REGEX);
  if (match) {
    return match[1];
  }
  return getQueryParam(GAME_QUERY_PARAM);
}
